import axios from "axios";

axios.defaults.withCredentials = true;
const ENV_BASE = process.env.REACT_APP_BACKEND_URL;
const siteOf = (host) => host.split(".").slice(-2).join(".");
// On a custom domain the same host also serves /api: calling it first-party avoids CORS and the
// cross-site cookies that Safari/iPhone block, so login keeps working there.
export const BASE = siteOf(new URL(ENV_BASE).hostname) === siteOf(window.location.hostname) ? ENV_BASE : window.location.origin;
export const API = `${BASE}/api`;

export const analyzeQuota = () => axios.get(`${API}/analyze/quota`).then((r) => r.data);
export const bundleCheckout = () => axios.post(`${API}/analyze/bundle/checkout`, { origin_url: window.location.origin }).then((r) => r.data);
export const bundleConfirm = (sid) => axios.get(`${API}/analyze/bundle/confirm/${encodeURIComponent(sid)}`).then((r) => r.data);
export const analyzeCheckout = (url, lang) => axios.post(`${API}/analyze/checkout`, { url, lang, origin_url: window.location.origin }).then((r) => r.data);
export const analyzeUrl = async (url, lang, paidSessionId) => {
  const { data } = await axios.post(`${API}/analyze`, { url, lang, paid_session_id: paidSessionId || null }, { timeout: 90000 });
  return data;
};

export const createCampaign = async (payload) => {
  const { data } = await axios.post(`${API}/campaigns`, payload);
  return data;
};

export const getCampaign = async (id) => {
  const { data } = await axios.get(`${API}/campaigns/${id}`);
  return data;
};

export const getCampaignLeads = async (id, count = 6) => {
  const { data } = await axios.get(`${API}/campaigns/${id}/leads?count=${count}`);
  return data;
};

export const listMyCampaigns = async () => {
  const { data } = await axios.get(`${API}/campaigns`);
  return data;
};

export const paymentStatus = async (session_id) => {
  const { data } = await axios.get(`${API}/payments/status/${session_id}`);
  return data;
};

export const googleSession = async (session_id) => {
  const { data } = await axios.post(`${API}/auth/google/session`, { session_id });
  return data;
};

export const getCatalog = () => axios.get(`${API}/catalog`).then(r => r.data);
export const suggestServices = (params) => axios.get(`${API}/catalog/suggest`, { params }).then(r => r.data);
export const createManagedCampaign = (payload) => axios.post(`${API}/campaigns/managed`, payload).then(r => r.data);
export const getCampaignReport = (id) => axios.get(`${API}/campaigns/${id}/report`).then(r => r.data);
export const contactsDownloadUrl = (id, fmt) => `${API}/campaigns/${id}/contacts.${fmt}`;
export const reportPdfUrl = (id) => `${API}/campaigns/${id}/report.pdf`;
export const renewCampaign = (id, currency, terms_accepted, discount_code) => axios.post(`${API}/campaigns/${id}/renew`, { currency, terms_accepted, discount_code, origin_url: window.location.origin }).then(r => r.data);
export const billingApi = {
  get: () => axios.get(`${API}/me/billing`).then(r => r.data),
  put: (payload) => axios.put(`${API}/me/billing`, payload).then(r => r.data),
};
export const validateDiscount = (code, amount) => axios.post(`${API}/discount-codes/validate`, { code, amount }).then(r => r.data);
export const invoicePdfUrl = (id) => `${API}/campaigns/${id}/invoice.pdf`;
export const notificationsApi = {
  list: () => axios.get(`${API}/notifications`).then(r => r.data),
  readAll: () => axios.post(`${API}/notifications/read-all`).then(r => r.data),
};

export const chatApi = {
  mine: () => axios.get(`${API}/chat/messages`).then(r => r.data),
  send: (text) => axios.post(`${API}/chat/messages`, { text }).then(r => r.data),
  unread: () => axios.get(`${API}/chat/unread`).then(r => r.data),
  typing: () => axios.post(`${API}/chat/typing`).then(r => r.data),
};

export const adminApi = {
  analysesHistory: (kind, month) => axios.get(`${API}/admin/analyses`, { params: { kind, month } }).then(r => r.data),
  catalogList: () => axios.get(`${API}/admin/catalog`).then(r => r.data),
  catalogCreate: (p) => axios.post(`${API}/admin/catalog`, p).then(r => r.data),
  catalogUpdate: (id, p) => axios.patch(`${API}/admin/catalog/${id}`, p).then(r => r.data),
  catalogDelete: (id) => axios.delete(`${API}/admin/catalog/${id}`).then(r => r.data),
  quickReplies: () => axios.get(`${API}/admin/chat/quick-replies`).then(r => r.data),
  addQuickReply: (p) => axios.post(`${API}/admin/chat/quick-replies`, p).then(r => r.data),
  editQuickReply: (id, p) => axios.put(`${API}/admin/chat/quick-replies/${id}`, p).then(r => r.data),
  deleteQuickReply: (id) => axios.delete(`${API}/admin/chat/quick-replies/${id}`).then(r => r.data),
  broadcast: (p) => axios.post(`${API}/admin/chat/broadcast`, p).then(r => r.data),
  broadcastPreview: (segment) => axios.get(`${API}/admin/chat/broadcast/preview`, { params: { segment } }).then(r => r.data),
  broadcasts: () => axios.get(`${API}/admin/chat/broadcasts`).then(r => r.data),
  chatTyping: (uid) => axios.post(`${API}/admin/chat/${uid}/typing`).then(r => r.data),
  deleteUsers: (user_ids) => axios.post(`${API}/admin/users/delete`, { user_ids }).then(r => r.data),
  chatThreads: () => axios.get(`${API}/admin/chat/threads`).then(r => r.data),
  chatThread: (uid) => axios.get(`${API}/admin/chat/${uid}/messages`).then(r => r.data),
  chatSend: (uid, text) => axios.post(`${API}/admin/chat/${uid}/messages`, { text }).then(r => r.data),
  transactions: () => axios.get(`${API}/admin/transactions`).then(r => r.data),
  refund: (sid) => axios.post(`${API}/admin/transactions/${sid}/refund`).then(r => r.data),
  invoices: (params) => axios.get(`${API}/admin/invoices`, { params }).then(r => r.data),
  invoicesCsvUrl: (params) => `${API}/admin/invoices.csv?${new URLSearchParams(Object.entries(params).filter(([, v]) => v))}`,
  invoicesXlsxUrl: (params) => `${API}/admin/invoices.xlsx?${new URLSearchParams(Object.entries(params).filter(([, v]) => v))}`,
  sendMonthlyInvoices: (year, month) => axios.post(`${API}/admin/invoices/send-monthly`, null, { params: { year, month } }).then(r => r.data),
  reportLog: () => axios.get(`${API}/admin/invoices/report-log`).then(r => r.data),
  invoicesMonthly: (year) => axios.get(`${API}/admin/invoices/monthly`, { params: { year } }).then(r => r.data),
  topClients: (params) => axios.get(`${API}/admin/invoices/top-clients`, { params }).then(r => r.data),
  discountCodes: () => axios.get(`${API}/admin/discount-codes`).then(r => r.data),
  createDiscountCode: (payload) => axios.post(`${API}/admin/discount-codes`, payload).then(r => r.data),
  toggleDiscountCode: (code, active) => axios.patch(`${API}/admin/discount-codes/${code}`, { active }).then(r => r.data),
  deleteDiscountCode: (code) => axios.delete(`${API}/admin/discount-codes/${code}`).then(r => r.data),
  managedAlerts: () => axios.get(`${API}/admin/managed-campaigns-alerts`).then(r => r.data),
  managedList: () => axios.get(`${API}/admin/managed-campaigns`).then(r => r.data),
  managedDetail: (id) => axios.get(`${API}/admin/managed-campaigns/${id}`).then(r => r.data),
  managedAction: (id, action) => axios.post(`${API}/admin/managed-campaigns/${id}/${action}`).then(r => r.data),
  updateDay: (id, day, payload) => axios.patch(`${API}/admin/managed-campaigns/${id}/days/${day}`, payload).then(r => r.data),
  importLeads: (id, day, lead_ids) => axios.post(`${API}/admin/managed-campaigns/${id}/days/${day}/import`, { lead_ids }).then(r => r.data),
  addItem: (id, day, payload) => axios.post(`${API}/admin/managed-campaigns/${id}/days/${day}/items`, payload).then(r => r.data),
  deleteItem: (id, day, kind, itemId) => axios.delete(`${API}/admin/managed-campaigns/${id}/days/${day}/items/${kind}/${itemId}`).then(r => r.data),
  users: () => axios.get(`${API}/admin/users`).then(r => r.data),
  orders: () => axios.get(`${API}/admin/orders`).then(r => r.data),
  campaigns: () => axios.get(`${API}/admin/campaigns`).then(r => r.data),
  deleteCampaigns: (campaign_ids) => axios.post(`${API}/admin/campaigns/delete`, { campaign_ids }).then(r => r.data),
  stats: () => axios.get(`${API}/admin/stats`).then(r => r.data),
  comms: () => axios.get(`${API}/admin/communications`).then(r => r.data),
  getKeys: () => axios.get(`${API}/admin/api-keys`).then(r => r.data),
  setKeys: (payload) => axios.put(`${API}/admin/api-keys`, payload).then(r => r.data),
  openRouterCredits: () => axios.get(`${API}/admin/api-keys/openrouter/credits`).then(r => r.data),
  aiUsage: (days) => axios.get(`${API}/admin/ai-usage`, { params: { days } }).then(r => r.data),
  setOpenRouterThreshold: (threshold) => axios.put(`${API}/admin/api-keys/openrouter/threshold`, { threshold }).then(r => r.data),
  testOpenRouter: () => axios.post(`${API}/admin/api-keys/openrouter/test`).then(r => r.data),
  // Lead Gen
  atecoSearch: (q, country = "") => axios.get(`${API}/admin/leadgen/ateco`, { params: { q, country } }).then(r => r.data),
  analyzeSite: (url) => axios.post(`${API}/admin/leadgen/analyze-site`, { url }).then(r => r.data),
  pdfUrl: (pdf_id) => `${API}/admin/leadgen/pdf/${pdf_id}`,
  estimate: (icp) => axios.post(`${API}/admin/leadgen/estimate`, icp).then(r => r.data),
  searchLeads: (payload) => axios.post(`${API}/admin/leadgen/search`, payload).then(r => r.data),
  startScrapeJob: (payload) => axios.post(`${API}/admin/leadgen/jobs`, payload).then(r => r.data),
  findDecisionMakers: (leadIds) => axios.post(`${API}/admin/leadgen/decision-makers`, { lead_ids: leadIds }).then(r => r.data),
  getScrapeJob: (id) => axios.get(`${API}/admin/leadgen/jobs/${id}`).then(r => r.data),
  listLeads: (params = {}) => axios.get(`${API}/admin/leadgen/leads`, { params }).then(r => r.data),
  mapLeads: () => axios.get(`${API}/admin/leadgen/leads/map`).then(r => r.data),
  updateCrm: (id, payload) => axios.patch(`${API}/admin/leadgen/leads/${id}/crm`, payload).then(r => r.data),
  followups: () => axios.get(`${API}/admin/leadgen/followups`).then(r => r.data),
  sendDigest: () => axios.post(`${API}/admin/leadgen/followups/send-digest`).then(r => r.data),
  placesUsage: () => axios.get(`${API}/admin/google-places/usage`).then(r => r.data),
  perplexityUsage: () => axios.get(`${API}/admin/perplexity/usage`).then(r => r.data),
  costSettings: () => axios.get(`${API}/admin/cost-settings`).then(r => r.data),
  setCostSettings: (payload) => axios.put(`${API}/admin/cost-settings`, payload).then(r => r.data),
  campaignCosts: () => axios.get(`${API}/admin/campaign-costs`).then(r => r.data),
  publishedLeads: (id) => axios.get(`${API}/admin/managed-campaigns/${id}/published-leads`).then(r => r.data),
  publishLead: (id, leadId, day) => axios.post(`${API}/admin/managed-campaigns/${id}/published-leads/${leadId}`, null, { params: { day } }).then(r => r.data),
  unpublishLead: (id, leadId) => axios.delete(`${API}/admin/managed-campaigns/${id}/published-leads/${leadId}`).then(r => r.data),
  setMarginCosts: (id, payload) => axios.put(`${API}/admin/margins/${id}`, payload).then(r => r.data),
  setAddonStatus: (id, aid, status) => axios.patch(`${API}/admin/managed-campaigns/${id}/addons/${aid}`, { status }).then(r => r.data),
  addonsPending: () => axios.get(`${API}/admin/addons/pending`).then(r => r.data),
  hmSites: () => axios.get(`${API}/admin/hm/sites`).then(r => r.data),
  hmAddSite: (p) => axios.post(`${API}/admin/hm/sites`, p).then(r => r.data),
  hmDelSite: (id) => axios.delete(`${API}/admin/hm/sites/${id}`).then(r => r.data),
  hmData: (params) => axios.get(`${API}/admin/hm/data`, { params }).then(r => r.data),
  margins: (month) => axios.get(`${API}/admin/margins`, { params: { month } }).then(r => r.data),
  apifyUsage: () => axios.get(`${API}/admin/apify/usage`).then(r => r.data),
  metaStatus: () => axios.get(`${API}/admin/meta/status`).then(r => r.data),
  metaCreate: (id) => axios.post(`${API}/admin/campaigns/${id}/meta/create`).then(r => r.data),
  metaAction: (id, action) => axios.post(`${API}/admin/campaigns/${id}/meta/${action}`).then(r => r.data),
  metaInsights: (id) => axios.get(`${API}/admin/campaigns/${id}/meta/insights`).then(r => r.data),
  waSend: (to, body) => axios.post(`${API}/admin/whatsapp/send`, { to, body }).then(r => r.data),
  leadSources: () => axios.get(`${API}/admin/leadgen/sources`).then(r => r.data),
  markContacted: (leadId, payload) => axios.post(`${API}/admin/leadgen/leads/${leadId}/mark-contacted`, payload).then(r => r.data),
  listPdfs: () => axios.get(`${API}/admin/leadgen/pdfs`).then(r => r.data),
  uploadPdf: (file, onProgress) => { const f = new FormData(); f.append("file", file); return axios.post(`${API}/admin/leadgen/pdfs/upload`, f, { onUploadProgress: (e) => onProgress?.(Math.round((e.loaded * 100) / (e.total || 1))) }).then(r => r.data); },
  downloadPdf: (id) => axios.get(`${API}/admin/leadgen/pdfs/${id}/download`, { responseType: "blob" }).then(r => r.data),
  deletePdf: (id) => axios.delete(`${API}/admin/leadgen/pdfs/${id}`).then(r => r.data),
  exportCsv: () => `${API}/admin/leadgen/export.csv`,
  exportXlsx: () => `${API}/admin/leadgen/export.xlsx`,
};
